import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, Route, Routes, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PortalError, clearPortalToken, getPortalToken, papi, setPortalLostHandler, setPortalToken, type PortalMe } from './api';
import { PortalLang, TEXT, usePortalText, type Lang } from './text';
import PortalList from './PortalList';
import PortalNew from './PortalNew';
import PortalTicket from './PortalTicket';
import { PortalStatement, PortalStatementList } from './PortalStatements';

// Portal del cliente: soporte.ascendix.com.mx/t/{empresa}. Marca de la empresa (Ascendix),
// español o inglés, entrada con código por correo.

export const pInput = 'h-11 w-full rounded-lg border border-[#CFD8DD] bg-white px-3 text-[15px] text-asx-navy placeholder:text-[#8A9AA3] focus:border-asx-navy focus:outline-none focus:ring-2 focus:ring-asx-navy/15';
export const pArea = 'w-full rounded-lg border border-[#CFD8DD] bg-white px-3 py-2.5 text-[15px] text-asx-navy placeholder:text-[#8A9AA3] focus:border-asx-navy focus:outline-none focus:ring-2 focus:ring-asx-navy/15';
export const pPrimary = 'inline-flex h-11 items-center justify-center rounded-lg bg-asx-navy px-5 text-[15px] font-semibold text-white hover:bg-[#0A2F43] disabled:opacity-50';
export const pSecondary = 'inline-flex h-11 items-center justify-center rounded-lg border border-[#CFD8DD] bg-white px-4 text-[15px] font-medium text-asx-navy hover:bg-[#F3F6F7] disabled:opacity-50';

const browserLang = (): Lang => (typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('en') ? 'en' : 'es');

export default function PortalApp() {
  return (
    <Routes>
      <Route path="/t/:slug/*" element={<PortalRoot />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function PortalRoot() {
  const { slug = '' } = useParams();
  const queryClient = useQueryClient();
  const [hasToken, setHasToken] = useState(() => Boolean(getPortalToken()));
  const [lang, setLangState] = useState<Lang>(() => {
    try { return (localStorage.getItem('vetaDeskPortalLang') as Lang) || browserLang(); } catch { return browserLang(); }
  });
  const me = useQuery({ queryKey: ['portal-me'], queryFn: () => papi<PortalMe>('/me'), enabled: hasToken, retry: false, staleTime: 60_000 });

  useEffect(() => { setPortalLostHandler(() => { setHasToken(false); queryClient.removeQueries({ queryKey: ['portal-me'] }); }); }, [queryClient]);
  // El idioma del usuario (lo define Ascendix al invitarlo) manda, salvo que lo cambie aquí.
  useEffect(() => { if (me.data?.locale) setLangState(me.data.locale); }, [me.data?.locale]);
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  const setLang = (l: Lang) => {
    setLangState(l);
    try { localStorage.setItem('vetaDeskPortalLang', l); } catch { /* sin storage */ }
    if (hasToken) void papi('/me/locale', { method: 'PUT', body: JSON.stringify({ locale: l }) }).catch(() => undefined);
  };
  const ctx = useMemo(() => ({ lang, t: TEXT[lang], setLang }), [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => {
    clearPortalToken();
    setHasToken(false);
    queryClient.removeQueries({ predicate: q => String(q.queryKey[0]).startsWith('portal') });
  };

  // Un token de otra empresa no sirve en este portal.
  const wrongTenant = me.data && me.data.tenantSlug !== slug;
  useEffect(() => { if (wrongTenant) logout(); }); // eslint-disable-line react-hooks/exhaustive-deps

  let body: ReactNode;
  if (hasToken && me.isLoading) body = <p role="status" className="py-24 text-center text-sm text-[#5B6B73]">{TEXT[lang].loading}</p>;
  else if (!hasToken || !me.data || wrongTenant) {
    body = <PortalLogin slug={slug} onToken={token => { setPortalToken(token); setHasToken(true); void queryClient.invalidateQueries({ queryKey: ['portal-me'] }); }} />;
  } else {
    body = (
      <Routes>
        <Route index element={<PortalList me={me.data} />} />
        <Route path="nuevo" element={<PortalNew me={me.data} />} />
        <Route path="tickets/:number" element={<PortalTicket me={me.data} />} />
        {me.data.role === 'approver' && <Route path="estados" element={<PortalStatementList />} />}
        {me.data.role === 'approver' && <Route path="estados/:projectId/:month" element={<PortalStatement />} />}
        <Route path="*" element={<Navigate to={`/t/${slug}`} replace />} />
      </Routes>
    );
  }

  return (
    <PortalLang.Provider value={ctx}>
      <PortalShell slug={slug} me={hasToken && !wrongTenant ? me.data ?? null : null} onLogout={logout}>{body}</PortalShell>
    </PortalLang.Provider>
  );
}

function PortalShell({ slug, me, onLogout, children }: { slug: string; me: PortalMe | null; onLogout: () => void; children: ReactNode }) {
  const { t, lang, setLang } = usePortalText();
  return (
    <div className="flex min-h-screen flex-col bg-[#F5F7F6] text-asx-navy">
      <header className="border-b border-[#E1E7E9] bg-white print:hidden">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <Link to={`/t/${slug}`} className="flex min-w-0 items-center gap-3" aria-label={t.portal}>
            <img src="/ascendix-logo.png" alt="Ascendix" className="h-8 w-auto sm:h-9" />
            <span className="hidden h-6 border-l border-[#D5DEE2] sm:block" aria-hidden="true" />
            {me?.organizationLogo
              ? <img src={me.organizationLogo} alt={me.organizationName} title={me.organizationName} className="h-8 max-w-[140px] object-contain sm:h-9" />
              : <span className="hidden truncate text-sm font-medium text-[#5B6B73] sm:block">{me ? me.organizationName : t.portal}</span>}
          </Link>
          <div className="flex items-center gap-1 sm:gap-3">
            <button type="button" onClick={() => setLang(lang === 'es' ? 'en' : 'es')}
              className="rounded-md px-2 py-1.5 text-sm text-[#5B6B73] hover:bg-[#F3F6F7] hover:text-asx-navy">{t.language}</button>
            {me && (
              <>
                <span className="hidden text-right text-sm leading-tight md:block">
                  <span className="block font-semibold">{me.name}</span>
                  <span className="block text-xs text-[#5B6B73]">{me.email}</span>
                </span>
                <button type="button" onClick={onLogout} className="rounded-md px-2 py-1.5 text-sm text-[#5B6B73] hover:bg-[#F3F6F7] hover:text-asx-navy">{t.signOut}</button>
              </>
            )}
          </div>
        </div>
        <div className="h-1 bg-gradient-to-r from-asx-green via-asx-green to-asx-navy" aria-hidden="true" />
      </header>
      <main className="mx-auto box-border w-full max-w-5xl flex-1 px-4 py-6 sm:px-6 sm:py-8">{children}</main>
      <footer className="px-4 pb-6 text-center text-xs text-[#8A9AA3] print:hidden">
        {me?.tenantName ?? 'Ascendix'} · {t.portal} · {t.poweredBy}
      </footer>
    </div>
  );
}

function PortalLogin({ slug, onToken }: { slug: string; onToken: (token: string) => void }) {
  const { t } = usePortalText();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const info = useQuery({ queryKey: ['portal-info', slug], queryFn: () => papi<{ name: string }>(`/info/${encodeURIComponent(slug)}`), retry: false });

  const ask = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true); setError('');
    try {
      await papi('/auth/code', { method: 'POST', body: JSON.stringify({ slug, email }) });
      setStep('code');
    } catch (err) {
      setError(err instanceof PortalError ? err.message : t.genericError);
    } finally { setBusy(false); }
  };
  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const r = await papi<{ token: string }>('/auth/verify', { method: 'POST', body: JSON.stringify({ slug, email, code: code.trim() }) });
      onToken(r.token);
    } catch (err) {
      setError(err instanceof PortalError ? err.message : t.genericError);
    } finally { setBusy(false); }
  };

  if (info.isError) {
    return <p className="mx-auto max-w-md rounded-xl bg-white p-8 text-center text-sm">{(info.error as Error).message}</p>;
  }

  return (
    <div className="mx-auto grid max-w-4xl items-center gap-8 py-6 md:grid-cols-[1.1fr_1fr] md:py-12">
      <div className="hidden md:block">
        <p className="text-sm font-semibold uppercase tracking-[0.14em] text-asx-green">{t.portal}</p>
        <h1 className="mt-3 text-4xl font-semibold leading-tight">{info.data?.name ?? ''}</h1>
        <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-[#5B6B73]">{t.signInBody}</p>
        <img src="/ascendix-icon.png" alt="" aria-hidden="true" className="mt-10 h-24 w-24 opacity-90" />
      </div>
      <section className="rounded-2xl border border-[#E1E7E9] bg-white p-6 shadow-sm sm:p-8">
        <h2 className="text-xl font-semibold">{t.signInTitle}</h2>
        <p className="mt-1.5 text-sm text-[#5B6B73] md:hidden">{t.signInBody}</p>
        {error && <p role="alert" className="mt-4 rounded-lg bg-[#FDECEC] px-3 py-2 text-sm text-[#8F1D1D]">{error}</p>}
        {step === 'email' ? (
          <form className="mt-5 flex flex-col gap-4" onSubmit={ask}>
            <label className="flex flex-col gap-1.5 text-sm font-medium">{t.email}
              <input type="email" required autoComplete="email" className={pInput} value={email} onChange={e => setEmail(e.target.value)} />
            </label>
            <button type="submit" className={pPrimary} disabled={busy || !email}>{busy ? t.sending : t.sendCode}</button>
          </form>
        ) : (
          <form className="mt-5 flex flex-col gap-4" onSubmit={verify}>
            <p className="rounded-lg bg-asx-soft px-3 py-2.5 text-sm text-[#2E4A1A]">{t.codeSent(email)}</p>
            <label className="flex flex-col gap-1.5 text-sm font-medium">{t.code}
              <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} required autoFocus
                className={`${pInput} text-center font-mono text-2xl tracking-[0.5em]`} value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))} />
            </label>
            <button type="submit" className={pPrimary} disabled={busy || code.length !== 6}>{busy ? t.sending : t.verify}</button>
            <div className="flex justify-between text-sm">
              <button type="button" className="text-[#3D6B8A] hover:underline" onClick={() => { setStep('email'); setCode(''); }}>{t.otherEmail}</button>
              <button type="button" className="text-[#3D6B8A] hover:underline" disabled={busy} onClick={() => void ask()}>{t.resend}</button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
